import { useCallback, useState } from "react";
import { PIIVerificationFormState } from "./PIIVerification";
import { useParams as useParamsBase } from "react-router-dom";
import {
  useVerifyClientIdentityMutation as useVerifyClientIdentityMutationBase,
  ConsentType,
} from "@grow-therapy-team/seedling-components";
import { convertToISODate } from "../utils";
import { useTrackIntroRoomEvent as useTrackIntroRoomEventBase } from "../page-content/useTrackIntroRoomEvent";
import { EventTypeEnum } from "../../../segment/types";

export type VerificationData = {
  fullLegalName: string;
  missingConsentTypes: ConsentType[];
  clientUserParentGuardianShortId?: string | null;
};

export type Dependencies = {
  useParams: typeof useParamsBase;
  useTrackIntroRoomEvent: typeof useTrackIntroRoomEventBase;
  useVerifyClientIdentityMutation: typeof useVerifyClientIdentityMutationBase;
};

/**
 * Custom hook for managing the state, logic, and callbacks for the PII
 * verification form. This hook is used to verify the client's identity and
 * retrieve the necessary data for the required agreements review.
 *
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The form state, the callback for submitting the form, and response
 * data after the form has been submitted.
 */
export function usePIIVerification(
  {
    useParams,
    useTrackIntroRoomEvent,
    useVerifyClientIdentityMutation,
  }: Dependencies = {
    useParams: useParamsBase,
    useTrackIntroRoomEvent: useTrackIntroRoomEventBase,
    useVerifyClientIdentityMutation: useVerifyClientIdentityMutationBase,
  },
) {
  const { patientShortId } = useParams();
  const { formSubmitted } = useTrackIntroRoomEvent();

  const [isFormValid, setFormIsValid] = useState(false);
  const [formState, setFormState] = useState<PIIVerificationFormState>({
    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
    phoneNumber: "",
  });
  const [serverError, setServerError] = useState<string | null>(null);
  const [verificationData, setVerificationData] =
    useState<VerificationData | null>(null);
  const [consentSigningToken, setConsentSigningToken] = useState<string | null>(
    null,
  );

  const [verifyClientIdentity, { loading: isSubmitting }] =
    useVerifyClientIdentityMutation({
      onError: (error) => {
        setServerError(error.message);
      },
    });

  /**
   * Callback for submitting the PII verification form. Returns a promise that
   * resolves to true if the form is submitted successfully, false otherwise.
   *
   * @param flowId - The flow ID to use for the verification.
   * @returns A promise that resolves to true if verification is successful,
   * false otherwise.
   */
  const handleSubmit = useCallback(
    async (flowId: string): Promise<boolean> => {
      if (!isFormValid || !patientShortId || isSubmitting) return false;
      const isoDate = convertToISODate(formState.dateOfBirth);
      if (!isoDate) return false;

      formSubmitted(
        EventTypeEnum.CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_SUBMITTED,
        { flowId },
      );
      setServerError(null);

      const { data } = await verifyClientIdentity({
        variables: {
          input: {
            ...formState,
            flowId,
            patientShortId,
            dateOfBirth: isoDate,
          },
        },
      });

      if (!data || !data.verifyClientIdentityForConsent) return false;

      const {
        consentSigningToken,
        fullLegalName,
        missingConsentTypes,
        clientUserParentGuardianShortId,
      } = data.verifyClientIdentityForConsent;
      setVerificationData({
        fullLegalName,
        missingConsentTypes: missingConsentTypes.filter(
          (consentType) => consentType !== null,
        ),
        clientUserParentGuardianShortId,
      });
      setConsentSigningToken(consentSigningToken);
      return true;
    },
    [
      formState,
      formSubmitted,
      isFormValid,
      isSubmitting,
      verifyClientIdentity,
      patientShortId,
    ],
  );

  return {
    formState,
    handleSubmit,
    isSubmitDisabled: !isFormValid || isSubmitting,
    isSubmitting,
    responseData: {
      consentSigningToken,
      verificationData,
      serverError,
    },
    setFormState,
    setFormIsValid,
  };
}
